<template>
    <div
        v-if="job && org"
        class="JobViewLayoutRight h-full space-y-8 border-t border-gray-400 pt-8 dark:border-default lg:space-y-18 lg:border-l lg:border-t-0 lg:pl-16 lg:pt-0"
    >
        <!-- Details section -->
        <div class="space-y-6">
            <div class="flex items-center justify-between">
                <CHeading :variant="2">
                    {{ $t('jobView.jobDetails.title', job.lang || 'sk') }}
                </CHeading>
                <small class="text-sm text-gray-450 dark:text-txt-body">
                    {{ $t('jobParameters.update', job.lang || 'sk') }}: {{ updatedAtDate }}
                </small>
            </div>

            <!-- Start date -->
            <div>
                <CHeading class="mb-2 text-lg" :variant="3">
                    {{ $t('jobView.boarding.title', job.lang || 'sk') }}
                </CHeading>

                {{ startDate }}
            </div>

            <!-- End date -->
            <div v-if="endDate">
                <CHeading class="mb-2 text-lg" :variant="3">
                    {{ $t('jobView.jobDetails.endDate', job.lang || 'sk') }}
                </CHeading>

                {{ endDate }}
            </div>

            <div v-if="job.who">
                <CHeading class="text-lg" :variant="3">
                    {{ $t('jobView.jobWho.title', job.lang || 'sk') }}
                </CHeading>

                <CList v-if="Array.isArray(job.who)" class="space-y-1">
                    <li v-for="who in job.who" :key="who" class="pl-2">
                        {{ $t(`jobView.jobWho.type.${who}`, job.lang || 'sk') }}
                    </li>
                </CList>
            </div>

            <div v-if="job.manager || job.managerLinkedin">
                <CHeading class="text-lg" :variant="3">
                    {{ $t('directManagerBlock.title', job.lang || 'sk') }}
                </CHeading>

                <DirectManagerBlock :lang="job.lang" :name="job.manager" :url="job.managerLinkedin" />
            </div>

            <div v-if="job.education">
                <CHeading class="text-lg" :variant="3">
                    {{ $t('jobView.jobDetails.preferredEducation.title', job.lang || 'sk') }}
                </CHeading>

                {{ $t(`jobView.jobDetails.preferredEducation.type.${job.education}`, job.lang || 'sk') }}
            </div>

            <div v-if="job.educationDetails">
                <CHeading class="text-lg" :variant="3">
                    {{ $t('jobView.jobDetails.educationDetails.title', job.lang || 'sk') }}
                </CHeading>

                {{ $t(`jobView.jobDetails.educationDetails.type.${job.educationDetails}`, job.lang || 'sk') }}
            </div>

            <div v-if="job.degree">
                <CHeading class="text-lg" :variant="3">
                    {{ $t('jobView.jobDetails.degree.title', job.lang || 'sk') }}
                </CHeading>

                {{ $t(`jobView.jobDetails.degree.type.${job.degree}`, job.lang || 'sk') }}
            </div>
        </div>

        <div class="space-y-6">
            <CHeading :variant="2">
                {{
                    $t(
                        isInitiative ? 'jobView.aboutInitiativeDetails.title' : 'jobView.aboutOrgDetails.title',
                        job.lang || 'sk'
                    )
                }}
            </CHeading>

            <div>
                <CHeading class="mb-1.5 text-lg leading-7" :variant="3">
                    {{ org.title }}
                </CHeading>

                <span class="dots leading-7" v-html="orgDescription" />
            </div>

            <CButton
                class="!px-4 font-medium"
                :event="preview ? '' : 'click'"
                icon="next"
                icon-right
                :to="{
                    name: 'orgs-id',
                    params: { id: org.slug || org.id }
                }"
                variant="primary-150"
            >
                {{
                    $t(
                        isInitiative
                            ? 'jobView.aboutInitiativeDetails.buttonShowMore'
                            : 'jobView.aboutOrgDetails.buttonShowMore',
                        job.lang || 'sk'
                    )
                }}
            </CButton>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { Job } from '#shared/types/Job'
import { StartType } from '#shared/types/Job'
import type { Org } from '#shared/types/Org'
import { OrgType } from '#shared/types/Org'
import { formatDate, formatObjectDate } from '#shared/utils'

interface Props {
    job?: Job
    org?: Org
    preview?: boolean
}

const props = defineProps<Props>()

const lang = computed(() => {
    return props.job?.lang || 'sk'
})

const startDate = computed(() => {
    if (!props.job) return ''
    return props.job.startType === StartType.ASAP
        ? useI18n().t('jobView.boarding.type.now', lang.value).toString()
        : formatObjectDate(props.job, 'startDate')
})

const endDate = computed(() => {
    return props.job ? formatObjectDate(props.job, 'endDate') : ''
})

const updatedAtDate = computed(() => {
    if (!props.job?.updatedAt) return ''
    return formatDate(props.job.updatedAt)
})

const isInitiative = computed(() => {
    return props.org?.type === OrgType.other
})

const orgDescription = computed(() => {
    if (!props.org?.desc) return ''
    const description = props.org.desc.replace(/<[^>]+>/g, '')
    return description.length > 200 ? description.substring(0, 200) + '..' : description
})
</script>

<style scoped>
.dots {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 7;
    -webkit-box-orient: vertical;
}
</style>
